import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DynamicTitle from "../components/DynamicTitle";
import SEOStructuredData from "../components/SEOStructuredData";

const Biography = () => {
  const location = useLocation();

  useEffect(() => {
    console.log("User visited Biography page:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#0a0a0a]">
      <DynamicTitle
        title="My Biography: From Zero to a Mission"
        description="From landing in Barcelona with €2000 to becoming an AI entrepreneur - the complete story of transformation, failure, and finding purpose."
        keywords="biography, entrepreneur journey, Spain, Armenia, AI, generalist, Amazon FBA, Upwork, freelancing"
        canonicalUrl="https://aramai.am/biography"
      />
      <SEOStructuredData
        type="article"
        title="My Biography: From Zero to a Mission"
        description="From landing in Barcelona with €2000 to becoming an AI entrepreneur - the complete story of transformation, failure, and finding purpose."
        url="https://aramai.am/biography"
        author="Aram AI"
        datePublished="2025-01-15"
      />
      <Header />
      <main className="flex-1">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold mb-8 text-center">
            My Biography: From Zero to a Mission
          </h1>

          <article className="glass-panel p-8 md:p-12 rounded-lg space-y-6">
            <p className="text-[17px] leading-[1.75] text-gray-200 font-article font-normal">
              On January 4, 2018, I landed in Barcelona with one piece of luggage and €2000. I had no plan, no profession, no job, and no friends waiting for me. I didn't even speak the language. It was like starting a game of GTA in real life, only in GTA, they at least speak English.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I had just left my entire old life behind - my family, my university, my car, my home. Why? Because a sudden change in law meant I would be forced into military service, a path I saw as a two-year waste of time. So I made an instant, crazy decision: I would move to Spain and start from scratch.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              That first year was the most horrible of my life. I was 21, depressed, broke, and stuck. My biggest problem? I was a generalist. I could do a little bit of everything, but I wasn't a "specialist" in anything. I had no clear label, no simple service to sell.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              For years, I saw this as my greatest weakness. Now, I know it's my greatest superpower. This is the story of how I learned to harness it.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold mt-12 mb-6">
              The Origin: A Generalist in the Making
            </h2>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              My story didn't start in Spain. It started in Yerevan. I was the kid teachers said had "great potential" but didn't use it, mostly because I'd only learn subjects if I liked the teacher. When we got a PC at home, my real education began. I was hooked on the internet, on strategy games like Ikariam and Civilization where I could govern countries and manage economies.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              My "builder" instinct showed up early. In 6th grade, I built a website for my class. It was a simple forum where we could post homework, discuss teachers, and share notes. At the same time, my father had several businesses over the years—a betshop, a game zone, and later a billiards shop. I was always around that environment, talking to clients and learning.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I tried everything: chess for about two years, tennis for a year, guitar for about six months. Nothing really stuck. Then, at 15, a random moment changed everything. One day, I heard Avicii's "Levels" on TV and was obsessed. I had to know how that music was made.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              After some research, I downloaded FL Studio, and for the next two years, music production became my life. I released 2 EPs, an album, and even started ghost-producing for a client. At 16, I was earning my own money, buying my own Pioneer mixers and an iPhone 5S, all on my own.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold mt-12 mb-6">
              University, Hustles, and a Life-Changing Law
            </h2>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              As my school years ended, the authority of the school itself became lower and lower in my eyes. I started skipping classes. When I thought about my future, Armenian society really only pushed one path: "be a programmer." So, I had that in the back of my mind.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              But when it was time to choose a university, I picked Philosophy. It was new, and I figured I could learn to code on the side. At the same time, I got a job. I was a live sports trader at 18. I even drove for GG Taxi, which was maybe the best job I've ever had - getting paid to drive, listen to music, and talk to people. So for a moment I was basically doing 2 jobs after the classes.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I was a builder, a producer, a strategist, a driver, and a philosophy student. I was a generalist, and I had no idea how valuable that would be.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              Then, in late 2017, the law that allowed me to skip the army was changed. I was faced with a two-year waste of time. The decision was made. I sold my car, got a visa, and on January 4, 2018, I was in Barcelona.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold mt-12 mb-6">
              The Crucible: Forged in Spain
            </h2>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              Arriving in Spain with no language and no plan, I was forced to survive. That first year in Barcelona was a blur of walking, listening to music, and feeling lost. Totally broke and depressed.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              The turning point came when a friend suggested Upwork. (You can read my full <Link to="/projects/upwork" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Upwork Story here</Link>). This is where being a generalist finally became an asset. But it was a grind.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I was still living illegally. I didn't even have a bank account - I'd withdraw my Upwork money to PayPal, find someone online who needed to buy something, pay for it, and get cash in return.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I moved to Valencia in 2019, and from that point, I started to systemize my life. Slowly, I built a life. I got long-term clients, which led to me getting my documents. Life got stable. But stable wasn't the goal. I saw an ad for Amazon FBA and knew that was it. I saved $13,000 and invested it all. (You can read the full story of my first FBA brand, its initial success, and its failure <Link to="/projects/amazon" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">on the Amazon FBA Page here</Link>).
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              That failure was a hard lesson. But it led me back to Upwork, this time as a high-value FBA "expert," now able to charge more. I was successful. I built an agency. I even partnered with an investor to build a new brand, "qbottle."
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              But I was stuck. I always knew my goal was bigger than just being a regular employee, and here I was, back to being a high-paid freelancer, working for other people's dreams. I was living a stable, "good" life, but I was unhappy.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold mt-12 mb-6">
              The Reboot: Finding the "Why"
            </h2>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              It was the summer of 2022. I was burned out, doing jobs I didn't like, and living in an unhealthy way. I watched a life-changing video on YouTube, and something just... clicked. My "MODE" changed.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              In one day, I quit everything.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I left my jobs (I had savings). I left my apartment. I quit sugar. I stopped smoking (and this time, after 2,388,324 tries, it stuck). I quit all my bad habits. It was stressful and incredibly hard. To make a clean break, I drove to Germany by car with a friend and lived there for two months. I was building a new version of myself.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I came back to Spain after those two months with a new lifestyle and a clear mind. And at that exact moment, the ChatGPT boom (November 2022) started to happen.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              This was it. This was the moment my entire life clicked into place. The kid who loved strategy games, the music producer who reverse-engineered complex software, the philosophy student who loved big ideas, the builder who made websites, and the generalist who could learn anything... this was the field I was built for.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I realized my true passion wasn't just e-commerce or freelancing; it was building systems and leveraging technology. (You can read the full story of my pivot to AI <Link to="/products/ai-automation" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">on the AI Automation Page here</Link>).
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              AI is the ultimate tool for a generalist. It's the ultimate force multiplier.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold mt-12 mb-6">
              The Mission: Coming Full Circle
            </h2>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              My life has been a series of pivots, failures, and lessons. It's taught me to be like water, to be liquid and adapt. After all these years, I finally found the niche I love. I get to work at the cutting edge of technology, build an audience, and create real value.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              But what's left?
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              My passport is expired. I'm still "wanted" in Armenia. But this 8-year chapter in Spain is closing. My next chapter is about going back to Armenia.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I've already started. My content, my business, my mission... it's all for the Armenian market. I want to empower my people with these tools. I disagree with the idea that "Armenia is too small." I'm here to prove that wrong.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              I'm writing this knowing that the next time I update it, I'll be writing about coming home.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold mt-12 mb-6">
              A Few More Things About Me
            </h2>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              <strong>Manchester United:</strong> I've been a fan since childhood. Sir Alex Ferguson is the reason.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              <strong>Music:</strong> I still love electronic music. It's what taught me how to build something from nothing.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              <strong>Driving:</strong> I'm super enthusiastic about cars and driving. It's like meditation for me.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              <strong>Finance:</strong> I reject traditional finance. 99% of my funds are in crypto.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              <strong>The Journey:</strong> In 7 years in Spain, I've moved 11 times and visited over 40 cities.
            </p>

            <p className="text-lg leading-[1.8] text-gray-300 font-article">
              <strong>My Purpose:</strong> I'm still learning every day, and I'm teaching what I learn through my YouTube, Instagram, and private community.
            </p>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Biography;
